/**
 * Portal Logger - Captures frontend events and sends them to backend
 */

const BUFFER_SIZE = 5; // Number of logs to batch before sending
let logBuffer = [];
let sendTimer = null;

const sendLogsToBackend = async (logs) => {
  // Get authorization token - only send logs if user is authenticated
  let token = null;
  try {
    token = localStorage.getItem('token');
  } catch (err) {
    console.error('Failed to get token from localStorage:', err);
  }

  // Don't send logs if user is not authenticated
  if (!token) {
    return;
  }

  // Get user email from localStorage
  let userEmail = null;
  try {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      userEmail = user.email;
    }
  } catch (err) {
    console.error('Failed to parse user from localStorage:', err);
  }

  for (const log of logs) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      headers['Authorization'] = `Bearer ${token}`;

      await fetch('/api/logs/portal', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          event: log.event,
          user: userEmail,
          details: log.details,
          level: log.level,
          url: window.location.pathname,
          userAgent: navigator.userAgent,
          createdBy: userEmail
        })
      }).catch(() => {
        // Silently fail - don't break user experience
      });
    } catch (err) {
      console.error('Failed to send portal log:', err);
    }
  }
};

const flushLogs = () => {
  if (logBuffer.length > 0) {
    sendLogsToBackend(logBuffer);
    logBuffer = [];
  }
  if (sendTimer) {
    clearTimeout(sendTimer);
    sendTimer = null;
  }
};

const scheduleLogFlush = () => {
  if (sendTimer) {
    clearTimeout(sendTimer);
  }
  // Send logs after 5 seconds of inactivity
  sendTimer = setTimeout(flushLogs, 5000);
};

export const portalLogger = {
  // Log user actions
  logAction: (event, details = '') => {
    logBuffer.push({
      event,
      details,
      level: 'INFO',
      timestamp: new Date().toISOString()
    });

    if (logBuffer.length >= BUFFER_SIZE) {
      flushLogs();
    } else {
      scheduleLogFlush();
    }
  },

  // Log errors
  logError: (event, error = '') => {
    logBuffer.push({
      event,
      details: error instanceof Error ? error.message : String(error),
      level: 'ERROR',
      timestamp: new Date().toISOString()
    });

    // Send errors immediately
    flushLogs();
  },

  // Log warnings
  logWarning: (event, details = '') => {
    logBuffer.push({
      event,
      details,
      level: 'WARNING',
      timestamp: new Date().toISOString()
    });

    if (logBuffer.length >= BUFFER_SIZE) {
      flushLogs();
    } else {
      scheduleLogFlush();
    }
  },

  // Manually flush pending logs
  flush: flushLogs
};

// Flush logs on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushLogs);
}
